import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Grid, Typography, Collapse, IconButton, Box, Table, TableHead, TableCell,
  TableRow, TableBody, TextField, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Select, MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ModalTransferList from './ModalTransferList';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

import { DailyReportActivity, DailyReportEmployee, DailyReportEquipment, DailyReportTower } from "../types/dailyReportTypes";
import { DailyReportSubcontract, ResourceType, Subcontractor } from "../types/subcontractTypes";
import SubcontractResourceTable from './SubcontractResourceTable';
import SubcontractPickerModal from './SubcontractPickerModal';
import { Tower } from "../types/towerTypes";
import { Employee } from "../types/EmployeeTypes";
import { Equipment } from "../types/equipmentTypes";
import { getTowersByProjectId, getTowersWithProgress } from '../api/towerApi';
import { ProjectActivity } from '../types/ActivityTypes';
import { ProjectDayHours } from '../types/projectDayHoursTypes';
import { getProjectDayHours } from '../api/projectDayHoursApi';

type SortDirection = 'asc' | 'desc';

const ZERO_PROGRESS_REASONS = [
  "Disponibilidad de material",
  "Disponibilidad de maquinaria",
  "Incidencia climatológicas",
  "Falta de planificación / Comunicación",
  "Ingeniería",
  "Problemas de acceso",
  "Retrabajos",
  "Seguridad y Salud",
  "Otros"
];

const getForwardSpan = (towerObj: any): number => {
  if (!towerObj) return 0;
  if (towerObj.forwardSpanM) return Number(towerObj.forwardSpanM);
  if (towerObj.forward_span_m) return Number(towerObj.forward_span_m);
  if (towerObj.forwardSpan) return Number(towerObj.forwardSpan);

  const key = Object.keys(towerObj).find(k =>
      k.toLowerCase().includes('forward') ||
      k.toLowerCase().includes('span') ||
      k.toLowerCase().includes('vano')
  );
  if (key) return Number(towerObj[key]);

  return 0;
};

/**
 * Suma las horas subcontratadas de un frente para un tipo de recurso.
 * Las horas de subcontrato se registran como total de la jornada, no por persona ni por
 * equipo, asi que se suman directamente sin multiplicar por las horas del dia.
 */
const sumSubcontractHours = (front: any, resourceType: ResourceType): number =>
  (front?.subcontractRows || [])
    .filter((row: DailyReportSubcontract) => row.resourceType === resourceType)
    .reduce((acc: number, row: DailyReportSubcontract) => acc + (Number(row.totalHours) || 0), 0);

/** Suma la dotacion (personal) o la cantidad (equipos) subcontratada de un frente. */
const sumSubcontractHeadcount = (front: any, resourceType: ResourceType): number =>
  (front?.subcontractRows || [])
    .filter((row: DailyReportSubcontract) => row.resourceType === resourceType)
    .reduce((acc: number, row: DailyReportSubcontract) => acc + (Number(row.headcount) || 0), 0);

/** Filas de subcontrato de un frente para un tipo de recurso, en el orden original. */
const getSubcontractRows = (front: any, resourceType: ResourceType): DailyReportSubcontract[] =>
  (front?.subcontractRows || []).filter((row: DailyReportSubcontract) => row.resourceType === resourceType);

const ActivityDetails: React.FC<{
  activity: any;
  index: number;
  towers: Tower[];
  project?: number;
  projectActivities?: ProjectActivity[];
  employees: Employee[];
  equipmentList: Equipment[];
  workFronts: { frontNumber: number; productionRows: DailyReportTower[]; personnelRows: DailyReportEmployee[]; equipmentRows: DailyReportEquipment[]; subcontractRows?: DailyReportSubcontract[]; observations: string; totalHh: number, totalHm: number }[];
  assignedEmployees: Set<string>;
  assignedEquipment: Set<number>;
  onAssignEmployee: (rut: string) => void;
  onAssignEquipment: (id: number) => void;
  unassignEmployee: (rut: string) => void;
  unassignEquipment: (id: number) => void;
  onChange: (index: number, updatedActivity: any) => void;
  onRemove?: () => void;
  activeColumnHormi?: boolean;
  onUpdateTotals: (index: number, hh: number, hm: number, production: Record<number, number>) => void;
  status?: string;
  onChangeStatus?: (newStatus: string) => void;
  isEdit?: boolean;
  reportDate: Date;
  onRegisterDeletedTower?: (id: number) => void;
}> = ({
  activity,
  index,
  towers,
  project,
  projectActivities,
  employees,
  equipmentList,
  workFronts: initialWorkFronts,
  assignedEmployees,
  assignedEquipment,
  onAssignEmployee,
  onAssignEquipment,
  unassignEmployee,
  unassignEquipment,
  onChange,
  onRemove,
  onUpdateTotals,
  status,
  onChangeStatus,
  isEdit,
  reportDate,
  onRegisterDeletedTower
}) => {
    const [expanded, setExpanded] = useState(false);
    const [workFronts, setWorkFronts] = useState(initialWorkFronts);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState<'personal' | 'equipo' | 'produccion'>('personal');
    const [modalData, setModalData] = useState<any[]>([]);
    const [activeFront, setActiveFront] = useState<number>(0);

    const [matrixHistoryCache, setMatrixHistoryCache] = useState<Record<string, any>>({});

    const [editingRow, setEditingRow] = useState<{ frontIndex: number; rowIndex: number } | null>(null);
    const [editingOvertime, setEditingOvertime] = useState<{ frontIndex: number; rowIndex: number } | null>(null);
    const [editingEquipmentOvertime, setEditingEquipmentOvertime] = useState<{ frontIndex: number; rowIndex: number } | null>(null);
    // Valor del input "aplicar a todos" de HH/HM Extra, por frente (solo para mostrarlo en la cabecera).
    const [blockOvertimePersonnel, setBlockOvertimePersonnel] = useState<Record<number, string>>({});
    const [blockOvertimeEquipment, setBlockOvertimeEquipment] = useState<Record<number, string>>({});

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const [projectDayHours, setProjectDayHours] = useState<ProjectDayHours[]>([]);

    // Modal "+ SUBCONTRATO". Guarda desde que area se abrio y sobre que frente aplica.
    const [subcontractModal, setSubcontractModal] = useState<{ open: boolean; resourceType: ResourceType; frontIndex: number }>({
        open: false,
        resourceType: 'PERSONNEL',
        frontIndex: 0
    });

    const [inputMode, setInputMode] = useState<'percentage' | 'advance'>('percentage');

    const [editValues, setEditValues] = useState<{
        progressPercentage: number;
        advance: number;
        naturalGroundVolumeVnt: number;
        outcropVolume: number;
        realAdvance: number;
        matrixProgress?: Record<string, number>;
        zeroProgressReason?: string;
    }>({
        progressPercentage: 0,
        advance: 0,
        naturalGroundVolumeVnt: 0,
        outcropVolume: 0,
        realAdvance: 0,
        matrixProgress: {},
        zeroProgressReason: ''
    });

    const [totalHh, setTotalHh] = useState<number>(0);
    const [totalHm, setTotalHm] = useState<number>(0);

    const [sortConfigPersonnel, setSortConfigPersonnel] = useState<{ key: string, direction: SortDirection } | null>(null);
    const [sortConfigEquipment, setSortConfigEquipment] = useState<{ key: string, direction: SortDirection } | null>(null);

    const isMatrixActivity = activity.activityName && (activity.activityName.trim() === "Riega de Piloto Conductor" || activity.activityName.trim() === "Tendido de Conductor");
    const matrixStructure: Record<string, string[]> = {};
    let totalPhasesCount = 0;

    if (isMatrixActivity && projectActivities) {
        const subs = projectActivities
            .filter(pa => pa.activity.name === activity.activityName && pa.activity.subActivity)
            .map(pa => pa.activity.subActivity as string);

        subs.forEach(sub => {
            const circMatch = sub.match(/circuito\s+\d+/i);
            const faseMatch = sub.match(/fase\s+\d+/i);
            if (circMatch && faseMatch) {
                const c = circMatch[0].toUpperCase();
                if (!matrixStructure[c]) matrixStructure[c] = [];
                matrixStructure[c].push(sub);
                totalPhasesCount++;
            }
        });
    }
    const circuitKeys = Object.keys(matrixStructure).sort();

    const getManHours = useCallback((dateInput?: Date | string): number => {
      const date = new Date(reportDate);
      const jsDay = date.getUTCDay();
      const dayOfWeek = jsDay === 0 ? 7 : jsDay;

      // Si hay horas configuradas del proyecto, usarlas
      if (projectDayHours.length > 0) {
        const config = projectDayHours.find(d => d.dayOfWeek === dayOfWeek);
        return config ? config.hours : 0;
      }

      // Fallback: solo si no hay configuración
      const cutOffDate = new Date(2026, 3, 27);

      if (dayOfWeek === 7) return 0;
      if (date >= cutOffDate) {
        if (dayOfWeek === 6) return 4;
        if (dayOfWeek === 4 || dayOfWeek === 5 ) return 7;
        return 8;
      }

      if (dayOfWeek === 6) return 5;
      return 8;
    }, [reportDate, projectDayHours]);

    const recalculateTotals = useCallback((currentFronts: any[]) => {
      let totalHH = 0;
      let totalHM = 0;
      const mh = getManHours();
      let productionByFront: Record<number, number> = {};

      currentFronts.forEach((front) => {
        // Recurso propio (por fila, con horas extra) mas recurso subcontratado (regla 4.4).
        // Las HH/HM de subcontrato vienen como total de la jornada, no por persona, por lo
        // que no se multiplican por las horas del dia.
        const frontHH = front.personnelRows.reduce((acc: number, row: any) => {
          return acc + mh + (Number(row.overtimeHours) || 0);
        }, 0) + sumSubcontractHours(front, 'PERSONNEL');
        totalHH += frontHH;
        const frontHM = front.equipmentRows.reduce((acc: number, row: any) => {
          return acc + mh + (Number(row.overtimeHours) || 0);
        }, 0) + sumSubcontractHours(front, 'EQUIPMENT');
        totalHM += frontHM;

        const frontProduction = front.productionRows.reduce((acc: any, row: any) => acc + (row.progressPercentage || 0), 0) / 100;
        productionByFront[front.frontNumber] = frontProduction;
      });

      setTotalHh(totalHH);
      setTotalHm(totalHM);
      onUpdateTotals(index, totalHH, totalHM, productionByFront);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getManHours, index, onUpdateTotals]);

    useEffect(() => { setWorkFronts(initialWorkFronts); }, [initialWorkFronts]);
    useEffect(() => { recalculateTotals(workFronts); }, [recalculateTotals, workFronts]);

    useEffect(() => {
      if (project) {
        getProjectDayHours(project).then(data => setProjectDayHours(data)).catch(() => {});
      }
    }, [project]);

    const activityToTowerAttribute: Record<string, keyof Tower | ((tower: Tower, subActivity?: string) => number | string)> = {
      "Excavación": "excavationVolume",
      "Hormigonado": "concreteVolume",
      "Construcción de caminos de acceso": (tower, subActivity) => {
        switch (subActivity) {
          case "Plano": return tower.lightRoadLength;
          case "Medio": return tower.mediumRoadLength;
          default: return tower.heavyRoadLength;
        }
      },
      "Acero de Refuerzo": (tower) => tower.steelWeight,
      "Clasificación de Torres": (tower) => tower.towerWeight / 1000,
      "Clasificacion de Torres": (tower) => tower.towerWeight / 1000,
      "Acopio de Torres": (tower) => tower.towerWeight / 1000,
      "Pre-armado de torres": (tower) => tower.towerWeight / 1000,
      "Montaje de torres": (tower) => tower.towerWeight / 1000,
      "Revisado de Torres": (tower) => tower.towerWeight / 1000,
      "Instalación de Separadores": (tower) => tower.forwardSpan / 1000,
      "Instalación de Balizas": (tower) => tower.forwardSpan / 1000,
      "Instalación avifauna": (tower) => tower.forwardSpan / 1000,
      "Riega de Piloto Conductor": (tower: any) => getForwardSpan(tower) / 1000,
      "Tendido de Conductor": (tower: any) => getForwardSpan(tower) / 1000,
      "Riega de Piloto OPGW": (tower) => tower.forwardSpan / 1000,
      "Tendido OPGW": (tower) => tower.forwardSpan / 1000,
      "Flechado OPGW": (tower) => tower.forwardSpan / 1000,
    };

    // Valor teórico por torre (avance físico total al 100%) para una fila de producción.
    // Primero usa el mapa por nombre; si la actividad no está mapeada, cae a una regla por
    // unidad: cualquier actividad medida en km usa el vano adelante (forwardSpan) de la torre.
    // Así el avance en km no queda en 0 al crear/renombrar actividades en km como
    // "Rematado Conductor" o "Riega de Cable Piloto OPGW".
    const resolveTheoretical = (tower: any, dra: any): number | undefined => {
      // Unidad física de la fila: la de avance si existe, si no la de producción.
      const act = dra?.activity || {};
      const rawUnit = (act.unitOfMeasurement && act.unitOfMeasurement !== "NA")
        ? act.unitOfMeasurement
        : act.unitOfMeasurementProduction;
      const unit = (rawUnit || "").toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

      // Reglas por unidad (con prioridad): robustas ante nombres desactualizados en el mapa.
      // - km    -> vano adelante de la torre (m -> km).
      // - vanos -> 1 vano por torre (cada tramo entre torres es un vano).
      if (unit.includes("km")) return getForwardSpan(tower) / 1000;
      if (unit.includes("vano")) return 1;

      // Resto de unidades (tn, m3, kg, m…): mapa por nombre de actividad.
      const attr = activityToTowerAttribute[dra?.activityName];
      if (attr !== undefined) {
        if (typeof attr === "string") {
          const value = tower[attr as keyof Tower];
          return typeof value === "number" ? value : undefined;
        }
        if (typeof attr === "function") {
          return Number(attr(tower, dra?.activity?.subActivity)) || 0;
        }
      }
      return undefined;
    };

    const unitOfMeasurement = activity.activity.unitOfMeasurement && activity.activity.unitOfMeasurement !== "NA"
      ? activity.activity.unitOfMeasurement
      : (activity.activity.unitOfMeasurementProduction && activity.activity.unitOfMeasurementProduction !== "NA")
        ? activity.activity.unitOfMeasurementProduction
        : null;

    const productionColumnTitle = unitOfMeasurement ? `Avance (${unitOfMeasurement})` : null;
    const isHormigonado = activity.activity.name === "Hormigonado";
    const isRoadConstruction = activity.activity.name.includes("Construcción de caminos");
    // Unidad de la actividad, normalizada igual que en resolveTheoretical.
    const normalizedUnit = ((activity.activity.unitOfMeasurement && activity.activity.unitOfMeasurement !== "NA")
      ? activity.activity.unitOfMeasurement
      : activity.activity.unitOfMeasurementProduction || "").toString().toLowerCase();

    // La columna "Teórico" se muestra cuando resolveTheoretical puede entregar un valor:
    // por unidad (km / vanos) o por el mapa de atributos de torre.
    const hasTheoreticalValue = !!activityToTowerAttribute[activity.activity.name]
      || normalizedUnit.includes("km")
      || normalizedUnit.includes("vano");

    const toggleExpand = () => setExpanded(!expanded);

    // Actividades medidas en torres: el teórico por torre es 1, el avance es la fracción de torre.
    const isProductionUnitMode = (!activity.activity.unitOfMeasurement || activity.activity.unitOfMeasurement === "NA") && activity.activity.unitOfMeasurementProduction && activity.activity.unitOfMeasurementProduction !== "NA";
    const isTowerUnit = isProductionUnitMode && activity.activity.unitOfMeasurementProduction?.toLowerCase().includes('torre');

    const getAdvanceForRow = (row: any, percentage?: number): number => {
      const pct = percentage !== undefined ? percentage : (row.progressPercentage || 0);
      if (isTowerUnit) return Math.round((pct / 100) * 100) / 100;
      const attr = activityToTowerAttribute[row.dailyReportActivity?.activityName || activity.activity.name];
      const theo = typeof attr === "string" ? row.tower[attr] : typeof attr === "function" ? attr(row.tower, row.dailyReportActivity?.activity?.subActivity) : 0;
      return theo ? Math.round(((pct / 100) * Number(theo)) * 100) / 100 : 0;
    };


    const filterAllowedTowers = (items: any[]) => {
      const allowedTowerNumbers = new Set(
        towers.map(tower => String(tower.towerNumber).trim())
      );

      return items.filter(item => {
        const towerNumber =
          item.tower?.towerNumber ??
          item.originalTowerNumber ??
          item.towerNumber;

        return allowedTowerNumbers.has(String(towerNumber).trim());
      });
    };

    const handleOpenModal = async (type: 'personal' | 'equipo' | 'produccion', frontIndex: number) => {
      let availableItems = [];

      if (type === 'personal') {
        availableItems = employees.filter(emp => emp.rut !== undefined && !assignedEmployees.has(emp.rut));
      } else if (type === 'equipo') {
        availableItems = equipmentList.filter(equip => equip.id !== undefined && !assignedEquipment.has(equip.id));
      } else if (type === 'produccion') {

        if (isMatrixActivity && project && projectActivities) {
            try {
                const phaseActivities = projectActivities.filter(pa => pa.activity.name === activity.activityName && pa.activity.subActivity);
                const historyMap: Record<string, any> = {};

                await Promise.all(phaseActivities.map(async (pa) => {
                    try {
                        if (pa.activity.id !== undefined) {
                            const hRes = await getTowersWithProgress(project, pa.activity.id);
                            hRes.forEach((h: any) => {
                                const tNum = String(h.tower.towerNumber).trim();
                                if (!historyMap[tNum]) historyMap[tNum] = {};
                                historyMap[tNum][pa.activity.subActivity!] = h.cumulativePercentage || 0;
                            });
                        }
                    } catch(e) {}
                }));

                setMatrixHistoryCache(historyMap);

                availableItems = towers.map(t => {
                    const tNum = String(t.towerNumber).trim();
                    let sum = 0; let count = 0;
                    if (historyMap[tNum]) {
                        Object.values(historyMap[tNum]).forEach((val: any) => { sum += Number(val); count++; });
                    }
                    const avg = totalPhasesCount > 0 ? (sum / totalPhasesCount) : 0;
                    return {
                        ...t,
                        cumulativePercentage: avg,
                        originalTowerNumber: t.towerNumber,
                        towerNumber: `${t.towerNumber} (Prom Acum: ${avg.toFixed(1)}%)`
                    };
                });
            } catch (error) {
                availableItems = towers.map(t => ({ ...t, cumulativePercentage: 0, originalTowerNumber: t.towerNumber, towerNumber: `${t.towerNumber} (0.0%)` }));
            }
        } else if (project && activity.activity.name.includes("Construcción de caminos")) {
          try {
            const fetchedTowersRoad = await getTowersByProjectId(project, activity.activity.subActivity);
            const historyResponse = await getTowersWithProgress(project, activity.activity.id);
            const filteredRoadTowers = filterAllowedTowers(fetchedTowersRoad);

              availableItems = filteredRoadTowers.map((roadTower: any) => {
              const match = historyResponse.find((h: any) => {
                  if (h.tower.id && roadTower.id && h.tower.id == roadTower.id) return true;
                  return String(h.tower.towerNumber).trim() === String(roadTower.towerNumber).trim();
              });
              const accumulated = match ? (match.cumulativePercentage || 0) : 0;
              return { ...roadTower, cumulativePercentage: accumulated, originalTowerNumber: roadTower.towerNumber, towerNumber: `${roadTower.towerNumber} (${accumulated.toFixed(1)}%)` };
          });
          } catch (error) {
            const fallbackTowers = await getTowersByProjectId(project, activity.activity.subActivity);
            availableItems = fallbackTowers.map((t: any) => ({ ...t, cumulativePercentage: 0, originalTowerNumber: t.towerNumber, towerNumber: `${t.towerNumber} (0.0%)` }));
          }
        }
        else if (project) {
           try {
             const towersWithHistoryWrapper = await getTowersWithProgress(project, activity.activity.id);
             const filteredHistory = filterAllowedTowers(towersWithHistoryWrapper);

            availableItems = filteredHistory.map(wrapper => ({
              ...wrapper.tower,
              cumulativePercentage: wrapper.cumulativePercentage,
              originalTowerNumber: wrapper.tower.towerNumber,
              towerNumber: `${wrapper.tower.towerNumber} (${(wrapper.cumulativePercentage || 0).toFixed(1)}%)`
            }));
           } catch (error) {
             availableItems = towers.map(t => ({ ...t, cumulativePercentage: 0, originalTowerNumber: t.towerNumber, towerNumber: `${t.towerNumber} (0.0%)` }));
           }
        } else {
          availableItems = towers.map(t => ({ ...t, cumulativePercentage: 0, originalTowerNumber: t.towerNumber, towerNumber: `${t.towerNumber} (0.0%)` }));
        }
      } else { availableItems = towers; }

      setModalType(type);
      setActiveFront(frontIndex);
      setModalData(availableItems);
      setModalOpen(true);
    };

    const calculateHoursForFront = (front: any) => {
      const mh = getManHours(reportDate);
      // Propio por fila (con horas extra) mas subcontratado, igual que en recalculateTotals.
      const hhForFront = front.personnelRows.reduce((acc: number, row: any) => {
        return acc + mh + (Number(row.overtimeHours) || 0);
      }, 0) + sumSubcontractHours(front, 'PERSONNEL');
      const hmForFront = front.equipmentRows.reduce((acc: number, row: any) => {
        return acc + mh + (Number(row.overtimeHours) || 0);
      }, 0) + sumSubcontractHours(front, 'EQUIPMENT');
      return { hhForFront, hmForFront };
    };

    /** Abre el modal "+ SUBCONTRATO" para el area y el frente indicados. */
    const handleOpenSubcontractModal = (resourceType: ResourceType, frontIndex: number) => {
      setSubcontractModal({ open: true, resourceType, frontIndex });
    };

    /** Aplica una transformacion sobre las filas de subcontrato de un frente. */
    const updateSubcontractRows = (
      frontIndex: number,
      transform: (rows: DailyReportSubcontract[]) => DailyReportSubcontract[]
    ) => {
      const updatedFronts = workFronts.map((front, fIdx) => {
        if (fIdx !== frontIndex) return front;
        return { ...front, subcontractRows: transform(front.subcontractRows || []) };
      });
      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
    };

    /** Agrega una empresa al frente desde el modal. */
    const handleAddSubcontract = (subcontractor: Subcontractor, headcount: number, totalHours: number) => {
      const { resourceType, frontIndex } = subcontractModal;
      updateSubcontractRows(frontIndex, (rows) => [
        ...rows,
        { subcontractor, resourceType, headcount, totalHours }
      ]);
      setSubcontractModal({ ...subcontractModal, open: false });
    };

    /**
     * Convierte el indice dentro de la sub-tabla filtrada al indice real dentro de
     * subcontractRows, que mezcla personal y equipos en una sola lista.
     */
    const resolveSubcontractIndex = (frontIndex: number, resourceType: ResourceType, filteredIndex: number): number => {
      const rows = workFronts[frontIndex]?.subcontractRows || [];
      let seen = -1;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].resourceType === resourceType) {
          seen++;
          if (seen === filteredIndex) return i;
        }
      }
      return -1;
    };

    const handleSubcontractHeadcountChange = (frontIndex: number, resourceType: ResourceType, filteredIndex: number, value: number) => {
      const realIndex = resolveSubcontractIndex(frontIndex, resourceType, filteredIndex);
      if (realIndex < 0) return;
      updateSubcontractRows(frontIndex, (rows) =>
        rows.map((row, i) => i === realIndex ? { ...row, headcount: Math.max(0, value) } : row));
    };

    const handleSubcontractHoursChange = (frontIndex: number, resourceType: ResourceType, filteredIndex: number, value: number) => {
      const realIndex = resolveSubcontractIndex(frontIndex, resourceType, filteredIndex);
      if (realIndex < 0) return;
      updateSubcontractRows(frontIndex, (rows) =>
        rows.map((row, i) => i === realIndex ? { ...row, totalHours: Math.max(0, value) } : row));
    };

    const handleSubcontractDelete = (frontIndex: number, resourceType: ResourceType, filteredIndex: number) => {
      const realIndex = resolveSubcontractIndex(frontIndex, resourceType, filteredIndex);
      if (realIndex < 0) return;
      updateSubcontractRows(frontIndex, (rows) => rows.filter((_, i) => i !== realIndex));
    };

    const calculateTotalProductionPercentage = (productionRows: any[]) => {
      return productionRows.reduce((total, row) => total + (row.progressPercentage || 0), 0) / 100;
    };

    const calculateTotalAdvance = (productionRows: DailyReportTower[]) => {
      return productionRows.reduce((total, row) => {
        if (isTowerUnit) return total + Math.round(((row.progressPercentage || 0) / 100) * 100) / 100;
        if (row.advance && row.advance > 0) return total + row.advance;
        const theoreticalValue = resolveTheoretical(row.tower, row.dailyReportActivity) || 0;
        return total + ((theoreticalValue * (row.progressPercentage || 0)) / 100);
      }, 0);
    };

    const handleObservationChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, frontIndex: number) => {
      const newObservation = event.target.value;
      const updatedFronts = workFronts.map((front, index) => index === frontIndex ? { ...front, observations: newObservation } : front);
      setWorkFronts(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
    };

    const handleOvertimeChange = (frontIndex: number, rowIndex: number, value: number) => {
      const overtimeValue = Math.max(0, value);
      const updatedFronts = workFronts.map((front, fIdx) => {
        if (fIdx !== frontIndex) return front;
        const updatedPersonnelRows = [...front.personnelRows];
        updatedPersonnelRows[rowIndex] = {
          ...updatedPersonnelRows[rowIndex],
          overtimeHours: overtimeValue
        } as any;
        return { ...front, personnelRows: updatedPersonnelRows };
      });
      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
    };

    const handleEquipmentOvertimeChange = (frontIndex: number, rowIndex: number, value: number) => {
      const overtimeValue = Math.max(0, value);
      const updatedFronts = workFronts.map((front, fIdx) => {
        if (fIdx !== frontIndex) return front;
        const updatedEquipmentRows = [...front.equipmentRows];
        updatedEquipmentRows[rowIndex] = {
          ...updatedEquipmentRows[rowIndex],
          overtimeHours: overtimeValue
        } as any;
        return { ...front, equipmentRows: updatedEquipmentRows };
      });
      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
    };

    /** Asigna las mismas HH Extra a TODO el personal de un frente (bloque). Cada fila
     *  se puede seguir editando individualmente después. */
    const handleOvertimeChangeAll = (frontIndex: number, value: number) => {
      const overtimeValue = Math.max(0, value);
      const updatedFronts = workFronts.map((front, fIdx) => {
        if (fIdx !== frontIndex) return front;
        const updatedPersonnelRows = front.personnelRows.map((r: any) => ({ ...r, overtimeHours: overtimeValue }));
        return { ...front, personnelRows: updatedPersonnelRows };
      });
      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
    };

    /** Asigna las mismas HM Extra a TODOS los equipos de un frente (bloque). */
    const handleEquipmentOvertimeChangeAll = (frontIndex: number, value: number) => {
      const overtimeValue = Math.max(0, value);
      const updatedFronts = workFronts.map((front, fIdx) => {
        if (fIdx !== frontIndex) return front;
        const updatedEquipmentRows = front.equipmentRows.map((r: any) => ({ ...r, overtimeHours: overtimeValue }));
        return { ...front, equipmentRows: updatedEquipmentRows };
      });
      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
    };

    const handleSaveItems = (selectedItems: any[]) => {
      const mh = getManHours();
      const newActivity: DailyReportActivity = {
        id: activity.id,
        activityName: activity.activity.name || "",
        workFront: activity.workFront,
        activityDate: activity.activityDate,
        dailyReport: activity.dailyReport,
        activity: activity.activity
      };

      const updatedFronts = [...workFronts];
      const targetFront = { ...updatedFronts[activeFront] };

      if (modalType === 'personal') {
        const newPersonnel = selectedItems.map((employee) => {
          onAssignEmployee(employee.rut);
          return { mh, employee, overtimeHours: 0, workFront: activeFront + 1, dailyReport: activity.dailyReport, dailyReportActivity: newActivity };
        });
        targetFront.personnelRows = [...targetFront.personnelRows, ...newPersonnel];
        const frontMh = targetFront.personnelRows.reduce((acc: number, row: any) => acc + mh + (Number(row.overtimeHours) || 0), 0);
        targetFront.totalHh = frontMh;
      } else if (modalType === 'equipo') {
        const newEquipment = selectedItems.map((equipment) => {
          onAssignEquipment(equipment.id);
          return { mh, equipment, overtimeHours: 0, workFront: activeFront + 1, dailyReport: activity.dailyReport, dailyReportActivity: newActivity };
        });
        targetFront.equipmentRows = [...targetFront.equipmentRows, ...newEquipment];
        targetFront.totalHm = targetFront.equipmentRows.reduce((acc: number, row: any) => acc + mh + (Number(row.overtimeHours) || 0), 0);
      } else if (modalType === 'produccion') {
        const newProduction = selectedItems.map((item: any) => {
          const history = item.cumulativePercentage || 0;
          const cleanItem = { ...item };
          if (cleanItem.originalTowerNumber) {
              cleanItem.towerNumber = cleanItem.originalTowerNumber;
              delete cleanItem.originalTowerNumber;
          }

          const tNum = String(cleanItem.towerNumber).trim();
          const phaseHistory = isMatrixActivity ? (matrixHistoryCache[tNum] || {}) : {};

          const initialMatrix: Record<string, number> = {};
          const initialCumulatives: Record<string, number> = {};
          const initialOriginals: Record<string, number> = {};
          const initialIds: Record<string, number> = {};

          if (isMatrixActivity) {
              Object.values(matrixStructure).flat().forEach(phase => {
                  initialMatrix[phase] = 0;
                  initialCumulatives[phase] = phaseHistory[phase] || 0;
                  initialOriginals[phase] = 0;
                  initialIds[phase] = 0;
              });
          }

          return {
            tower: cleanItem,
            progressPercentage: 0,
            advance: 0,
            unitOfMeasurement: 0,
            workFront: { frontNumber: activeFront + 1 } as any,
            dailyReport: activity.dailyReport,
            dailyReportActivity: newActivity,
            cumulativeAdvance: 0,
            cumulativePercentage: history,
            naturalGroundVolumeVnt: 0,
            outcropVolume: 0,
            realAdvance: 0,
            matrixProgress: initialMatrix,
            phaseCumulatives: initialCumulatives,
            phaseOriginalProgress: initialOriginals,
            phaseIds: initialIds,
            zeroProgressReason: ''
          };
        });
        targetFront.productionRows = [...targetFront.productionRows, ...newProduction];
      }

      updatedFronts[activeFront] = targetFront;
      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
      setModalOpen(false);
    };

    const handleDeleteRow = (type: 'personal' | 'equipo' | 'produccion', frontIndex: number, rowIndex: number) => {
      const mh = getManHours();
      const updatedFronts = workFronts.map((front, fIdx) => {
        if (fIdx !== frontIndex) return front;
        const newFront = { ...front };

        if (type === 'personal') {
          const newPersonnelRows = [...newFront.personnelRows];
          unassignEmployee(newPersonnelRows[rowIndex].employee.rut || '');
          newPersonnelRows.splice(rowIndex, 1);
          newFront.personnelRows = newPersonnelRows;
          newFront.totalHh = newPersonnelRows.reduce((acc: number, row: any) => acc + mh + (Number(row.overtimeHours) || 0), 0);
        } else if (type === 'equipo') {
          const newEquipmentRows = [...newFront.equipmentRows];
          unassignEquipment(newEquipmentRows[rowIndex].equipment.id);
          newEquipmentRows.splice(rowIndex, 1);
          newFront.equipmentRows = newEquipmentRows;
          newFront.totalHm = newEquipmentRows.reduce((acc: number, row: any) => acc + mh + (Number(row.overtimeHours) || 0), 0);
        } else if (type === 'produccion') {
        const newProductionRows = [...newFront.productionRows];
        const rowToDeleteId = newProductionRows[rowIndex].id;

        if (rowToDeleteId !== undefined && rowToDeleteId > 0 && onRegisterDeletedTower) {
           onRegisterDeletedTower(rowToDeleteId);
        }
        newProductionRows.splice(rowIndex, 1);
        newFront.productionRows = newProductionRows;
        }
        return newFront;
      });

      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
    };

    const handleAddWorkFront = () => {
      const newFront = { frontNumber: workFronts.length + 1, productionRows: [], personnelRows: [], equipmentRows: [], subcontractRows: [], observations: "", totalHh: 0, totalHm: 0 };
      const reorganizedFronts = [...workFronts, newFront].map((front, index) => ({ ...front, frontNumber: index + 1 }));
      setWorkFronts(reorganizedFronts);
      recalculateTotals(reorganizedFronts);
      onChange(index, { ...activity, workFronts: reorganizedFronts, workFront: reorganizedFronts.length });
    };

    const handleDeleteSpecificFront = (indexToRemove: number) => {
      if (workFronts.length <= 1) return;
      const frontToRemove = workFronts[indexToRemove];
      frontToRemove.personnelRows.forEach(row => { if (row.employee.rut) unassignEmployee(row.employee.rut); });
      frontToRemove.equipmentRows.forEach(row => { if (row.equipment.id) unassignEquipment(row.equipment.id); });

      const reorganizedFronts = workFronts.filter((_, index) => index !== indexToRemove).map((front, index) => ({ ...front, frontNumber: index + 1 }));
      setWorkFronts(reorganizedFronts);
      recalculateTotals(reorganizedFronts);
      onChange(index, { ...activity, workFront: reorganizedFronts.length, workFronts: reorganizedFronts });
    };

    const handleEditClick = (frontIndex: number, rowIndex: number, rowData: any) => {
      setEditingRow({ frontIndex, rowIndex });
      setInputMode('percentage');
      setEditValues({
        progressPercentage: rowData.progressPercentage,
        advance: rowData.advance,
        naturalGroundVolumeVnt: rowData.naturalGroundVolumeVnt || 0,
        outcropVolume: rowData.outcropVolume || 0,
        realAdvance: rowData.realAdvance || 0,
        matrixProgress: rowData.matrixProgress || {},
        zeroProgressReason: rowData.zeroProgressReason || ''
      });
    };

    // Ingreso por porcentaje: recalcula el avance en unidad física.
    const handlePercentageInput = (value: number, row?: any) => {
      const percentage = Math.max(0, value);
      let advance = 0;
      if (row) {
        if (isTowerUnit) {
          advance = Math.round((percentage / 100) * 100) / 100;
        } else {
          const theoreticalValue = resolveTheoretical(row.tower, row.dailyReportActivity) || 0;
          advance = theoreticalValue ? Math.round(((percentage / 100) * theoreticalValue) * 100) / 100 : 0;
        }
      }
      setInputMode('percentage');
      setEditValues({ ...editValues, progressPercentage: percentage, advance });
    };

    // Ingreso por unidad de avance: recalcula el porcentaje contra el teórico de la torre.
    const handleAdvanceInput = (value: number, row?: any) => {
      const advance = Math.max(0, value);
      let percentage = 0;
      if (isTowerUnit) {
        percentage = Math.round(advance * 100 * 100) / 100;
      } else if (row) {
        const theoreticalValue = resolveTheoretical(row.tower, row.dailyReportActivity) || 0;
        percentage = theoreticalValue ? Math.round(((advance / theoreticalValue) * 100) * 100) / 100 : 0;
      }
      setInputMode('advance');
      setEditValues({ ...editValues, progressPercentage: percentage, advance });
    };

    const handleMatrixInputChange = (phaseKey: string, val: number) => {
        const updatedMatrix = { ...editValues.matrixProgress, [phaseKey]: val };

        let sumDaily = 0;
        Object.values(updatedMatrix).forEach(v => sumDaily += Number(v));
        const dailyAvg = totalPhasesCount > 0 ? sumDaily / totalPhasesCount : 0;

        setEditValues({
            ...editValues,
            matrixProgress: updatedMatrix,
            progressPercentage: dailyAvg
        });
    };

    const handleSaveClick = (frontIndex: number, rowIndex: number) => {
        const targetFront = workFronts[frontIndex];
        const targetRow = targetFront.productionRows[rowIndex];

        const currentSavedProgress = targetRow.progressPercentage || 0;
        const totalAccumulated = targetRow.cumulativePercentage || 0;
        const floorBase = totalAccumulated - currentSavedProgress;

        const newInput = editValues.progressPercentage;
        const projectedTotal = floorBase + newInput;

        if (projectedTotal > 100.01) {
          alert(`Error: El avance total acumulado (${projectedTotal.toFixed(2)}%) no puede superar el 100%.`);
          return;
        }

      const updatedFronts = workFronts.map((front, fIdx) => {
        if (fIdx !== frontIndex) return front;

        const updatedProductionRows = [...front.productionRows];
        const row = updatedProductionRows[rowIndex];

        const theoreticalValue = resolveTheoretical(row.tower, row.dailyReportActivity);

        // Si el usuario ingresó el avance por unidad, se respeta ese valor;
        // si ingresó el porcentaje, el avance se deriva del teórico de la torre.
        const newAdvance = isTowerUnit
          ? Math.round((editValues.progressPercentage / 100) * 100) / 100
          : editValues.advance > 0
            ? editValues.advance
            : (theoreticalValue && editValues.progressPercentage
              ? Math.round(((editValues.progressPercentage / 100) * theoreticalValue) * 100) / 100
              : 0);

        const newCumulative = floorBase + editValues.progressPercentage;

        updatedProductionRows[rowIndex] = {
          ...row,
          progressPercentage: editValues.progressPercentage,
          advance: newAdvance,
          naturalGroundVolumeVnt: editValues.naturalGroundVolumeVnt,
          outcropVolume: editValues.outcropVolume,
          realAdvance: editValues.realAdvance,
          cumulativePercentage: newCumulative,
          matrixProgress: editValues.matrixProgress,
          zeroProgressReason: (!editValues.progressPercentage || editValues.progressPercentage === 0)
                              ? editValues.zeroProgressReason
                              : null
        } as any;

        return { ...front, productionRows: updatedProductionRows };
      });

      setWorkFronts(updatedFronts);
      recalculateTotals(updatedFronts);
      onChange(index, { ...activity, workFronts: updatedFronts });
      setEditingRow(null);
    };

    const handleCancelClick = () => setEditingRow(null);

    // Los contadores incluyen la dotacion subcontratada, para que el TOTAL de la caja de
    // resumen y los indicadores de la cabecera cuadren con las horas mostradas.
    const calculateTotalByFront = (front: any) => ({
      totalEmployees: front.personnelRows.length + sumSubcontractHeadcount(front, 'PERSONNEL'),
      totalEquipment: front.equipmentRows.length + sumSubcontractHeadcount(front, 'EQUIPMENT')
    });

    const calculateTotalByActivity = () => {
      let totalEmployees = 0; let totalEquipment = 0;
      workFronts.forEach((front) => {
        totalEmployees += front.personnelRows.length + sumSubcontractHeadcount(front, 'PERSONNEL');
        totalEquipment += front.equipmentRows.length + sumSubcontractHeadcount(front, 'EQUIPMENT');
      });
      return { totalEmployees, totalEquipment };
    };

    const handleSortPersonnel = (key: string) => {
      let direction: SortDirection = 'asc';
      if (sortConfigPersonnel && sortConfigPersonnel.key === key && sortConfigPersonnel.direction === 'asc') direction = 'desc';
      setSortConfigPersonnel({ key, direction });
    };

    const handleSortEquipment = (key: string) => {
      let direction: SortDirection = 'asc';
      if (sortConfigEquipment && sortConfigEquipment.key === key && sortConfigEquipment.direction === 'asc') direction = 'desc';
      setSortConfigEquipment({ key, direction });
    };

    const { totalEmployees: totalEmployeesAct, totalEquipment: totalEquipmentAct } = calculateTotalByActivity();

    return (
      <Box sx={{ borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#99BFD9', p: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Etapa:</Typography>
              <Box sx={{ p: 1, bgcolor: '#E8E8F6', fontSize: '0.875rem', borderRadius: 1, textAlign: 'center' }}>
                <Typography noWrap>{activity.activity.section}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Actividad:</Typography>
              <Box sx={{ p: 1, bgcolor: '#E8E8F6', fontSize: '0.875rem', borderRadius: 1, textAlign: 'center' }}>
                <Typography noWrap>{activity.activityName}</Typography>
              </Box>
            </Grid>

            {!isMatrixActivity && activity.activity.subActivity && (
              <Grid item xs={12} sm={2}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Subactividad:</Typography>
                <Box sx={{ p: 1, bgcolor: '#E8E8F6', fontSize: '0.875rem', borderRadius: 1, textAlign: 'center' }}>
                  <Typography noWrap>{activity.activity.subActivity}</Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sm={1}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Frentes:</Typography>
              <Box sx={{ p: 1, bgcolor: '#E8E8F6', fontSize: '0.875rem', borderRadius: 1, textAlign: 'center' }}>
                <Typography>{workFronts.length}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={2}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Total empleados:</Typography>
              <Box sx={{ p: 1, bgcolor: '#E8E8F6', fontSize: '0.875rem', borderRadius: 1, textAlign: 'center' }}>
                <Typography noWrap>{totalEmployeesAct} emp. ({totalHh} HH)</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={2}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Total equipos:</Typography>
              <Box sx={{ p: 1, bgcolor: '#E8E8F6', fontSize: '0.875rem', borderRadius: 1, textAlign: 'center' }}>
                <Typography noWrap>{totalEquipmentAct} eq. ({totalHm} HM)</Typography>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12} sm="auto" container justifyContent="flex-end">
            <IconButton onClick={toggleExpand}>{expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}</IconButton>
            {(status !== 'view') && (
              <IconButton onClick={() => setConfirmDeleteOpen(true)} sx={{ color: '#FF5733' }}>
                <DeleteIcon />
              </IconButton>
            )}
          </Grid>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {workFronts.map((front, frontIndex) => {
            const { hhForFront, hmForFront } = calculateHoursForFront(front);
            const { totalEmployees, totalEquipment } = calculateTotalByFront(front);

            const sortedPersonnelRows = [...front.personnelRows].sort((a, b) => {
              if (!sortConfigPersonnel) return 0;
              const { key, direction } = sortConfigPersonnel;
              const aValue = String(a.employee[key as keyof typeof a.employee] || '').toLowerCase();
              const bValue = String(b.employee[key as keyof typeof b.employee] || '').toLowerCase();
              if (aValue < bValue) return direction === 'asc' ? -1 : 1;
              if (aValue > bValue) return direction === 'asc' ? 1 : -1;
              return 0;
            });

            const sortedEquipmentRows = [...front.equipmentRows].sort((a, b) => {
              if (!sortConfigEquipment) return 0;
              const { key, direction } = sortConfigEquipment;
              const aValue = String(a.equipment[key as keyof typeof a.equipment] || '').toLowerCase();
              const bValue = String(b.equipment[key as keyof typeof b.equipment] || '').toLowerCase();
              if (aValue < bValue) return direction === 'asc' ? -1 : 1;
              if (aValue > bValue) return direction === 'asc' ? 1 : -1;
              return 0;
            });

            return (
              <Box key={frontIndex} sx={{ bgcolor: '#D9D9D9', p: 3, borderTopLeftRadius: 1, borderTopRightRadius: 2 }}>
                <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2, border: '1px solid #ddd' }}>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Frente de Trabajo {front.frontNumber}:</Typography>
                      {(status !== 'view') && workFronts.length > 1 && (
                        <IconButton onClick={() => handleDeleteSpecificFront(frontIndex)} sx={{ color: '#FF5733' }} title="Eliminar este frente"><DeleteIcon /></IconButton>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }}>Empleados: {totalEmployees} ({hhForFront} HH)</Typography>
                      <Typography sx={{ fontWeight: 'bold' }}>Equipos: {totalEquipment} ({hmForFront} HM)</Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    {activity.activity.addProduction && (
                      <Grid item xs={12} sm={12}>
                        <Grid container alignItems="center" justifyContent="space-between">
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>PRODUCCIÓN:</Typography>
                          {(status !== 'view') && (
                            <Button variant="contained" sx={{ backgroundColor: '#004F86', ':hover': { backgroundColor: '#003B63' }, mb: 2 }} onClick={() => handleOpenModal('produccion', frontIndex)}>Agregar Producción</Button>
                          )}
                        </Grid>

                        <Box sx={{ overflowX: 'auto', width: '100%', mt: 2 }}>
                          {isMatrixActivity ? (
                            <Table sx={{ minWidth: '600px' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={2} sx={{ borderTopLeftRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold', borderRight: '1px solid white' }}>Torre</TableCell>
                                        {circuitKeys.map(c => (
                                            <TableCell key={c} colSpan={matrixStructure[c].length} align="center" sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold', borderRight: '1px solid white', borderBottom: '1px solid white' }}>
                                                {c}
                                            </TableCell>
                                        ))}
                                        <TableCell rowSpan={2} align="center" sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Avance Total (%)</TableCell>
                                        {productionColumnTitle && <TableCell rowSpan={2} align="center" sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>{productionColumnTitle}</TableCell>}

                                        <TableCell rowSpan={2} align="center" sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Motivo (Avance 0)</TableCell>

                                        {(status !== 'view') && <TableCell rowSpan={2} sx={{ borderTopRightRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Acción</TableCell>}
                                    </TableRow>
                                    <TableRow>
                                        {circuitKeys.map(c => matrixStructure[c].map(phaseSub => {
                                            const f = phaseSub.match(/fase\s+\d+/i)?.[0].toUpperCase() || phaseSub;
                                            return <TableCell key={phaseSub} align="center" sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold', borderRight: '1px solid white' }}>{f}</TableCell>;
                                        }))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {front.productionRows.map((row, i) => {
                                        const isEditing = editingRow?.frontIndex === frontIndex && editingRow.rowIndex === i;

                                        let sumAcum = 0;
                                        let sumDaily = 0;
                                        let phaseCount = 0;

                                        return (
                                            <TableRow key={i} sx={{ backgroundColor: 'white' }}>
                                                <TableCell><Typography variant="body2" sx={{fontWeight: 'bold'}}>{row.tower.towerNumber}</Typography></TableCell>

                                                {circuitKeys.map(c => matrixStructure[c].map(phaseSub => {
                                                    const rowAny = row as any;
                                                    const oldCumulative = rowAny.phaseCumulatives?.[phaseSub] || 0;
                                                    const oldProgress = rowAny.phaseOriginalProgress?.[phaseSub] || 0;
                                                    const historyVal = oldCumulative - oldProgress;

                                                    const dailyVal = isEditing ? (editValues.matrixProgress?.[phaseSub] || 0) : (rowAny.matrixProgress?.[phaseSub] || 0);
                                                    const totalProjected = historyVal + dailyVal;

                                                    sumAcum += totalProjected;
                                                    sumDaily += dailyVal;
                                                    phaseCount++;

                                                    return (
                                                        <TableCell key={phaseSub} align="center" sx={{ borderRight: '1px solid #f0f0f0', p: 1 }}>
                                                            {isEditing ? (
                                                                <TextField
                                                                    value={dailyVal === 0 ? "" : dailyVal}
                                                                    onChange={(e) => handleMatrixInputChange(phaseSub, Number(e.target.value))}
                                                                    type="number"
                                                                    inputProps={{ min: 0, max: 100 - historyVal, step: "any" }}
                                                                    size="small"
                                                                    sx={{ width: '60px' }}
                                                                    error={totalProjected > 100.01}
                                                                />
                                                            ) : (
                                                                <Typography sx={{ fontWeight: dailyVal > 0 ? 'bold' : 'normal', fontSize: '0.875rem', color: dailyVal > 0 ? '#1976d2' : 'inherit' }}>
                                                                    {dailyVal > 0 ? `+${dailyVal}%` : '-'}
                                                                </Typography>
                                                            )}
                                                            <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                                                                Acum: {totalProjected.toFixed(1)}%
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                }))}

                                                <TableCell align="center">
                                                    {(() => {
                                                        const accumulatedAvg = phaseCount > 0 ? (sumAcum / phaseCount) : 0;
                                                        const dailyAvg = phaseCount > 0 ? (sumDaily / phaseCount) : 0;

                                                        return (
                                                            <Box>
                                                                <Typography sx={{ fontWeight: 'bold', color: isEditing ? '#ed6c02' : 'inherit' }}>
                                                                    {accumulatedAvg.toFixed(2)}%
                                                                </Typography>
                                                                <Typography variant="caption" display="block" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                                                    Diario: {dailyAvg.toFixed(2)}%
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })()}
                                                </TableCell>

                                                {productionColumnTitle && (
                                                    <TableCell align="center">
                                                    {(() => {
                                                        const theoreticalValue = resolveTheoretical(row.tower, row.dailyReportActivity);

                                                        const accumulatedAvg = phaseCount > 0 ? (sumAcum / phaseCount) : 0;
                                                        const dailyAvg = phaseCount > 0 ? (sumDaily / phaseCount) : 0;

                                                        const acumKm = theoreticalValue ? ((accumulatedAvg / 100) * Number(theoreticalValue)).toFixed(4) : "0.0000";
                                                        const dailyKm = theoreticalValue ? ((dailyAvg / 100) * Number(theoreticalValue)).toFixed(4) : "0.0000";

                                                        return (
                                                            <Box>
                                                                <Typography sx={{ fontWeight: 'bold', color: isEditing ? '#ed6c02' : 'inherit' }}>
                                                                    {acumKm}
                                                                </Typography>
                                                                <Typography variant="caption" display="block" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                                                    Diario: {dailyKm}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })()}
                                                    </TableCell>
                                                )}

                                                <TableCell align="center">
                                                    {isEditing ? (
                                                        (!editValues.progressPercentage || editValues.progressPercentage === 0) ? (
                                                        <Select
                                                            value={editValues.zeroProgressReason || ''}
                                                            onChange={(e) => setEditValues({ ...editValues, zeroProgressReason: e.target.value as string })}
                                                            size="small"
                                                            displayEmpty
                                                            sx={{ minWidth: 150 }}
                                                        >
                                                            <MenuItem value="" disabled><em>Seleccione motivo</em></MenuItem>
                                                            {ZERO_PROGRESS_REASONS.map((r, idx) => <MenuItem key={idx} value={r}>{r}</MenuItem>)}
                                                        </Select>
                                                        ) : (
                                                        <Typography variant="caption" color="textSecondary">No aplica</Typography>
                                                        )
                                                    ) : (
                                                        (row.progressPercentage === 0 && row.zeroProgressReason) ? row.zeroProgressReason : "-"
                                                    )}
                                                </TableCell>

                                                {(status !== 'view') && (
                                                    <TableCell>
                                                    {isEditing ? (
                                                        <>
                                                        <IconButton onClick={() => handleSaveClick(frontIndex, i)} color="primary"><SaveIcon /></IconButton>
                                                        <IconButton onClick={handleCancelClick} color="primary"><CancelIcon /></IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                        <IconButton onClick={() => handleEditClick(frontIndex, i, row)} color="primary"><EditIcon /></IconButton>
                                                        <IconButton onClick={() => handleDeleteRow('produccion', frontIndex, i)} color="primary"><DeleteIcon /></IconButton>
                                                        </>
                                                    )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                          ) : (
                            <Table sx={{ minWidth: '600px' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ borderTopLeftRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Número de Torre</TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Producción (%)</TableCell>
                                  {productionColumnTitle && (
                                    <>
                                      <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>{productionColumnTitle}</TableCell>
                                      {hasTheoreticalValue && <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Teórico</TableCell>}
                                    </>
                                  )}
                                  {isHormigonado && (
                                    <>
                                      <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold', minWidth: '100px'}}>V.N.T. (m3)</TableCell>
                                      <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Volumen Afloramiento (m3)</TableCell>
                                    </>
                                  )}
                                  {isRoadConstruction && <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Avance Real (m)</TableCell>}

                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Motivo (Avance 0)</TableCell>

                                  {(status !== 'view') && <TableCell sx={{ borderTopRightRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Acción</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {front.productionRows.map((row, i) => {
                                  const projectedTotal = (row.cumulativePercentage || 0) - (row.progressPercentage || 0) + (editingRow?.rowIndex === i ? editValues.progressPercentage : row.progressPercentage);
                                  const isEditing = editingRow?.frontIndex === frontIndex && editingRow.rowIndex === i;
                                  return (
                                  <TableRow key={i} sx={{ backgroundColor: 'white' }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Typography variant="body2">{row.tower.towerNumber}</Typography>
                                          <Typography variant="caption" color="textSecondary" sx={{ ml: 1, fontStyle: 'italic' }}>({projectedTotal.toFixed(1)}%)</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                      {isEditing ? (
                                        <TextField
                                          value={editValues.progressPercentage === 0 && inputMode !== 'percentage' ? "" : editValues.progressPercentage}
                                          onChange={(e) => handlePercentageInput(Number(e.target.value), row)}
                                          type="number"
                                          inputProps={{ min: 0, step: "any" }}
                                          error={projectedTotal > 100.01}
                                          sx={{ backgroundColor: inputMode === 'percentage' ? '#e3f2fd' : 'white' }}
                                          helperText={inputMode === 'percentage' ? 'Editando' : 'Auto'}
                                        />
                                      ) : row.progressPercentage}
                                    </TableCell>
                                    {productionColumnTitle && (
                                      <>
                                        <TableCell>
                                          {isEditing ? (
                                            isTowerUnit ? (
                                              <TextField
                                                value={editValues.advance === 0 && inputMode !== 'advance' ? "" : Number(editValues.advance.toFixed(2))}
                                                onChange={(e) => handleAdvanceInput(Number(e.target.value), row)}
                                                type="number"
                                                inputProps={{ min: 0, step: "any" }}
                                                size="small"
                                                sx={{ backgroundColor: inputMode === 'advance' ? '#e3f2fd' : 'white' }}
                                                helperText={inputMode === 'advance' ? 'Editando' : 'Auto'}
                                              />
                                            ) : (
                                            <TextField
                                              value={editValues.advance === 0 && inputMode !== 'advance' ? "" : Number(editValues.advance.toFixed(4))}
                                              onChange={(e) => handleAdvanceInput(Number(e.target.value), row)}
                                              type="number"
                                              inputProps={{ min: 0, step: "any" }}
                                              size="small"
                                              sx={{ backgroundColor: inputMode === 'advance' ? '#e3f2fd' : 'white' }}
                                              helperText={inputMode === 'advance' ? 'Editando' : 'Auto'}
                                            />
                                            )
                                          ) : (
                                            (() => {
                                              if (isTowerUnit) return Number((row.progressPercentage || 0) / 100).toFixed(2);
                                              if (row.advance && row.advance > 0) return Number(row.advance).toFixed(2);
                                              const theo = resolveTheoretical(row.tower, row.dailyReportActivity);
                                              return theo && row.progressPercentage ? ((row.progressPercentage / 100) * Number(theo)).toFixed(2) : "0.00";
                                            })()
                                          )}
                                        </TableCell>
                                        {hasTheoreticalValue && (
                                          <TableCell>{(() => { const theo = isTowerUnit ? 1 : resolveTheoretical(row.tower, row.dailyReportActivity); return theo !== undefined ? Number(theo).toFixed(2) : "N/A"; })()}</TableCell>
                                        )}
                                      </>
                                    )}
                                    {isHormigonado && (
                                      <>
                                        <TableCell>{isEditing ? (projectedTotal >= 99.99 ? <TextField value={editValues.naturalGroundVolumeVnt || 0} onChange={(e) => setEditValues({ ...editValues, naturalGroundVolumeVnt: Number(e.target.value) })} type="number" size="small" /> : <Typography variant="caption" color="textSecondary">Requiere 100%</Typography>) : ((row.cumulativePercentage >= 99.99 || projectedTotal >= 99.99) ? row.naturalGroundVolumeVnt : "-")}</TableCell>
                                        <TableCell>{isEditing ? (projectedTotal >= 99.99 ? <TextField value={editValues.outcropVolume || 0} onChange={(e) => setEditValues({ ...editValues, outcropVolume: Number(e.target.value) })} type="number" size="small" /> : <Typography variant="caption" color="textSecondary">Requiere 100%</Typography>) : ((row.cumulativePercentage >= 99.99 || projectedTotal >= 99.99) ? row.outcropVolume : "-")}</TableCell>
                                      </>
                                    )}
                                    {isRoadConstruction && (
                                      <TableCell>{isEditing ? (projectedTotal >= 99.99 ? <TextField value={editValues.realAdvance || 0} onChange={(e) => setEditValues({ ...editValues, realAdvance: Number(e.target.value) })} type="number" size="small" /> : <Typography variant="caption" color="textSecondary">Requiere 100%</Typography>) : ((row.cumulativePercentage >= 99.99 || projectedTotal >= 99.99) ? row.realAdvance : "-")}</TableCell>
                                    )}

                                    <TableCell>
                                        {isEditing ? (
                                            (!editValues.progressPercentage || editValues.progressPercentage === 0) ? (
                                            <Select
                                                value={editValues.zeroProgressReason || ''}
                                                onChange={(e) => setEditValues({ ...editValues, zeroProgressReason: e.target.value as string })}
                                                size="small"
                                                displayEmpty
                                                sx={{ minWidth: 150 }}
                                            >
                                                <MenuItem value="" disabled><em>Seleccione motivo</em></MenuItem>
                                                {ZERO_PROGRESS_REASONS.map((r, idx) => <MenuItem key={idx} value={r}>{r}</MenuItem>)}
                                            </Select>
                                            ) : (
                                            <Typography variant="caption" color="textSecondary">No aplica</Typography>
                                            )
                                        ) : (
                                            (row.progressPercentage === 0 && row.zeroProgressReason) ? row.zeroProgressReason : "-"
                                        )}
                                    </TableCell>

                                    {(status !== 'view') && (
                                      <TableCell>
                                        {isEditing ? (
                                          <><IconButton onClick={() => handleSaveClick(frontIndex, i)} color="primary"><SaveIcon /></IconButton><IconButton onClick={handleCancelClick} color="primary"><CancelIcon /></IconButton></>
                                        ) : (
                                          <><IconButton onClick={() => handleEditClick(frontIndex, i, row)} color="primary"><EditIcon /></IconButton><IconButton onClick={() => handleDeleteRow('produccion', frontIndex, i)} color="primary"><DeleteIcon /></IconButton></>
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                  );
                                })}
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Total Producción</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>{calculateTotalProductionPercentage(front.productionRows).toFixed(2)}</TableCell>
                                  {productionColumnTitle && <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>{calculateTotalAdvance(front.productionRows).toFixed(2)}</TableCell>}
                                  {hasTheoreticalValue && <TableCell />}
                                  {isHormigonado && <><TableCell /><TableCell /></>}
                                  {isRoadConstruction && <TableCell />}
                                  <TableCell /> {/* Espacio vacío para la nueva columna "Motivo" */}
                                  {(status !== 'view') && <TableCell />}
                                </TableRow>
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={12}>
                      <TextField label="Observaciones" name="observations" value={front.observations || ''} onChange={(e) => handleObservationChange(e, frontIndex)} sx={{ backgroundColor: 'white' }} multiline fullWidth rows={3} disabled={status === 'view'} />
                    </Grid>

                    {activity.activity.addMh && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Grid container alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>PERSONAL:</Typography>
                            {(status !== 'view') && (
                              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Button variant="contained" sx={{ backgroundColor: '#004F86' }} onClick={() => handleOpenModal('personal', frontIndex)}>Agregar Personal</Button>
                                <Button variant="outlined" sx={{ borderColor: '#8a6d00', color: '#8a6d00' }} onClick={() => handleOpenSubcontractModal('PERSONNEL', frontIndex)}>+ Subcontrato</Button>
                              </Box>
                            )}
                          </Grid>
                          {/* Sub-tabla de recursos propios. Lleva rotulo propio y se oculta cuando
                              no tiene filas, igual que la de subcontrato: si no, su cabecera queda
                              vacia justo encima de las lineas subcontratadas y se lee como si fueran
                              de ella. Son sub-tablas independientes (regla 4.1 del documento). */}
                          {front.personnelRows.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#004F86', mb: 0.5 }}>
                            PERSONAL INTERNO (PROPIO)
                          </Typography>
                          <Box sx={{ overflowX: 'auto', width: '100%' }}>
                            <Table sx={{ minWidth: '100%' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ borderTopLeftRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}><TableSortLabel active={sortConfigPersonnel?.key === 'rut'} direction={sortConfigPersonnel?.key === 'rut' ? sortConfigPersonnel.direction : 'asc'} onClick={() => handleSortPersonnel('rut')}>RUT</TableSortLabel></TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}><TableSortLabel active={sortConfigPersonnel?.key === 'name'} direction={sortConfigPersonnel?.key === 'name' ? sortConfigPersonnel.direction : 'asc'} onClick={() => handleSortPersonnel('name')}>Nombre</TableSortLabel></TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}><TableSortLabel active={sortConfigPersonnel?.key === 'position'} direction={sortConfigPersonnel?.key === 'position' ? sortConfigPersonnel.direction : 'asc'} onClick={() => handleSortPersonnel('position')}>Cargo</TableSortLabel></TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <span>HH Extra</span>
                                      {status !== 'view' && front.personnelRows.length > 0 && (
                                        <TextField
                                          type="number"
                                          size="small"
                                          placeholder="Todos"
                                          title="Asigna estas HH Extra a todo el personal del frente (luego puedes ajustar fila por fila)"
                                          value={blockOvertimePersonnel[frontIndex] ?? ''}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setBlockOvertimePersonnel((prev) => ({ ...prev, [frontIndex]: v }));
                                            if (v !== '') handleOvertimeChangeAll(frontIndex, Number(v));
                                          }}
                                          inputProps={{ min: 0, max: 12, step: 0.5 }}
                                          sx={{ width: '90px', backgroundColor: 'white', borderRadius: 1 }}
                                        />
                                      )}
                                    </Box>
                                  </TableCell>
                                  {(status !== 'view') && <TableCell sx={{ borderTopRightRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Acción</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sortedPersonnelRows.map((row, i) => {
                                  const originalIndex = front.personnelRows.findIndex(r => r.employee.rut === row.employee.rut);
                                  return (
                                    <TableRow key={row.employee.rut || i} sx={{ backgroundColor: 'white' }}>
                                      <TableCell>{row.employee.rut}</TableCell>
                                      <TableCell>{`${row.employee.name || ''} ${row.employee.firstName || ''}`.trim()}</TableCell>
                                      <TableCell>{row.employee.position}</TableCell>
                                      <TableCell>
                                        {status !== 'view' ? (
                                          editingOvertime?.frontIndex === frontIndex && editingOvertime?.rowIndex === originalIndex ? (
                                            <TextField
                                              type="number"
                                              size="small"
                                              autoFocus
                                              value={(row as any).overtimeHours || ''}
                                              onChange={(e) => handleOvertimeChange(frontIndex, originalIndex, Number(e.target.value))}
                                              onBlur={() => setEditingOvertime(null)}
                                              inputProps={{ min: 0, max: 12, step: 0.5 }}
                                              sx={{ width: '80px' }}
                                            />
                                          ) : (
                                            <Box
                                              onClick={() => setEditingOvertime({ frontIndex, rowIndex: originalIndex })}
                                              sx={{ cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1, minWidth: '40px', textAlign: 'center', '&:hover': { backgroundColor: '#e3f2fd' } }}
                                            >
                                              <Typography variant="body2">{(row as any).overtimeHours || 0}</Typography>
                                            </Box>
                                          )
                                        ) : (
                                          (row as any).overtimeHours || 0
                                        )}
                                      </TableCell>
                                      {(status !== 'view') && <TableCell><IconButton onClick={() => handleDeleteRow('personal', frontIndex, originalIndex)} color='primary'><DeleteIcon /></IconButton></TableCell>}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                          </Box>
                          )}

                          <SubcontractResourceTable
                            rows={getSubcontractRows(front, 'PERSONNEL')}
                            resourceType="PERSONNEL"
                            status={status}
                            onChangeHeadcount={(rowIndex, value) => handleSubcontractHeadcountChange(frontIndex, 'PERSONNEL', rowIndex, value)}
                            onChangeTotalHours={(rowIndex, value) => handleSubcontractHoursChange(frontIndex, 'PERSONNEL', rowIndex, value)}
                            onDelete={(rowIndex) => handleSubcontractDelete(frontIndex, 'PERSONNEL', rowIndex)}
                          />

                          {/* Con las dos sub-tablas ocultas el area quedaria en blanco, asi que se
                              deja un texto que confirme que no falta nada por cargar. */}
                          {front.personnelRows.length === 0 && getSubcontractRows(front, 'PERSONNEL').length === 0 && (
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                              Sin personal registrado en este frente.
                            </Typography>
                          )}

                          {/* Caja de resumen del area. Las lineas en cero no se muestran, segun la regla 4.4. */}
                          {(front.personnelRows.length > 0 || getSubcontractRows(front, 'PERSONNEL').length > 0) && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, p: 1, backgroundColor: '#eaf2fa', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                {front.personnelRows.length > 0 && (
                                  <Typography variant="body2">
                                    Propio: {front.personnelRows.length} ({front.personnelRows.length * getManHours(reportDate)} HH)
                                  </Typography>
                                )}
                                {getSubcontractRows(front, 'PERSONNEL').length > 0 && (
                                  <Typography variant="body2" sx={{ color: '#8a6d00' }}>
                                    Subcto: {sumSubcontractHeadcount(front, 'PERSONNEL')} ({sumSubcontractHours(front, 'PERSONNEL')} HH)
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
                                TOTAL: {totalEmployees} Emp ({hhForFront} HH)
                              </Typography>
                            </Box>
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Grid container alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>EQUIPOS:</Typography>
                            {(status !== 'view') && (
                              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Button variant="contained" sx={{ backgroundColor: '#004F86' }} onClick={() => handleOpenModal('equipo', frontIndex)}>Agregar Equipo</Button>
                                <Button variant="outlined" sx={{ borderColor: '#8a6d00', color: '#8a6d00' }} onClick={() => handleOpenSubcontractModal('EQUIPMENT', frontIndex)}>+ Subcontrato</Button>
                              </Box>
                            )}
                          </Grid>
                          {/* Misma logica que en el area de personal: rotulo propio y la sub-tabla
                              se oculta si no tiene filas. */}
                          {front.equipmentRows.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#004F86', mb: 0.5 }}>
                            EQUIPOS INTERNOS (PROPIO/ARRENDADO)
                          </Typography>
                          <Box sx={{ overflowX: 'auto', width: '100%' }}>
                            <Table sx={{ minWidth: '100%' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ borderTopLeftRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>
                                    <TableSortLabel
                                      active={sortConfigEquipment?.key === 'vehicleNumber'}
                                      direction={sortConfigEquipment?.key === 'vehicleNumber' ? sortConfigEquipment.direction : 'asc'}
                                      onClick={() => handleSortEquipment('vehicleNumber')}
                                    >
                                      Patente
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}><TableSortLabel active={sortConfigEquipment?.key === 'name'} direction={sortConfigEquipment?.key === 'name' ? sortConfigEquipment.direction : 'asc'} onClick={() => handleSortEquipment('name')}>Equipo</TableSortLabel></TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Propiedad</TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Chofer</TableCell>
                                  <TableCell sx={{ backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <span>HM Extra</span>
                                      {status !== 'view' && front.equipmentRows.length > 0 && (
                                        <TextField
                                          type="number"
                                          size="small"
                                          placeholder="Todos"
                                          title="Asigna estas HM Extra a todos los equipos del frente (luego puedes ajustar fila por fila)"
                                          value={blockOvertimeEquipment[frontIndex] ?? ''}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setBlockOvertimeEquipment((prev) => ({ ...prev, [frontIndex]: v }));
                                            if (v !== '') handleEquipmentOvertimeChangeAll(frontIndex, Number(v));
                                          }}
                                          inputProps={{ min: 0, max: 12, step: 0.5 }}
                                          sx={{ width: '90px', backgroundColor: 'white', borderRadius: 1 }}
                                        />
                                      )}
                                    </Box>
                                  </TableCell>
                                  {(status !== 'view') && <TableCell sx={{ borderTopRightRadius: 10, backgroundColor: '#78a1bf', color: '#004F86', fontWeight: 'bold' }}>Acción</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sortedEquipmentRows.map((row, i) => {
                                  const originalIndex = front.equipmentRows.findIndex(r => r.equipment.id === row.equipment.id);
                                  return (
                                    <TableRow key={row.equipment.id || i} sx={{ backgroundColor: 'white' }}>
                                      <TableCell>{row.equipment.vehicleNumber}</TableCell>
                                      <TableCell>{row.equipment.name}</TableCell>
                                      <TableCell>{row.equipment.owner ? 'Propio' : 'Arrendado'}</TableCell>
                                      <TableCell>{row.equipment.employee?.name}</TableCell>
                                      <TableCell>
                                        {status !== 'view' ? (
                                          editingEquipmentOvertime?.frontIndex === frontIndex && editingEquipmentOvertime?.rowIndex === originalIndex ? (
                                            <TextField
                                              type="number"
                                              size="small"
                                              autoFocus
                                              value={(row as any).overtimeHours || ''}
                                              onChange={(e) => handleEquipmentOvertimeChange(frontIndex, originalIndex, Number(e.target.value))}
                                              onBlur={() => setEditingEquipmentOvertime(null)}
                                              inputProps={{ min: 0, max: 12, step: 0.5 }}
                                              sx={{ width: '80px' }}
                                            />
                                          ) : (
                                            <Box
                                              onClick={() => setEditingEquipmentOvertime({ frontIndex, rowIndex: originalIndex })}
                                              sx={{ cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1, minWidth: '40px', textAlign: 'center', '&:hover': { backgroundColor: '#e3f2fd' } }}
                                            >
                                              <Typography variant="body2">{(row as any).overtimeHours || 0}</Typography>
                                            </Box>
                                          )
                                        ) : (
                                          (row as any).overtimeHours || 0
                                        )}
                                      </TableCell>
                                      {(status !== 'view') && <TableCell><IconButton onClick={() => handleDeleteRow('equipo', frontIndex, originalIndex)} color='primary'><DeleteIcon /></IconButton></TableCell>}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                          </Box>
                          )}

                          <SubcontractResourceTable
                            rows={getSubcontractRows(front, 'EQUIPMENT')}
                            resourceType="EQUIPMENT"
                            status={status}
                            onChangeHeadcount={(rowIndex, value) => handleSubcontractHeadcountChange(frontIndex, 'EQUIPMENT', rowIndex, value)}
                            onChangeTotalHours={(rowIndex, value) => handleSubcontractHoursChange(frontIndex, 'EQUIPMENT', rowIndex, value)}
                            onDelete={(rowIndex) => handleSubcontractDelete(frontIndex, 'EQUIPMENT', rowIndex)}
                          />

                          {/* Con las dos sub-tablas ocultas el area quedaria en blanco. */}
                          {front.equipmentRows.length === 0 && getSubcontractRows(front, 'EQUIPMENT').length === 0 && (
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                              Sin equipos registrados en este frente.
                            </Typography>
                          )}

                          {/* Caja de resumen del area. Las lineas en cero no se muestran, segun la regla 4.4. */}
                          {(front.equipmentRows.length > 0 || getSubcontractRows(front, 'EQUIPMENT').length > 0) && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, p: 1, backgroundColor: '#eaf2fa', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                {front.equipmentRows.length > 0 && (
                                  <Typography variant="body2">
                                    Propio: {front.equipmentRows.length} ({front.equipmentRows.length * getManHours(reportDate)} HM)
                                  </Typography>
                                )}
                                {getSubcontractRows(front, 'EQUIPMENT').length > 0 && (
                                  <Typography variant="body2" sx={{ color: '#8a6d00' }}>
                                    Subcto: {sumSubcontractHeadcount(front, 'EQUIPMENT')} ({sumSubcontractHours(front, 'EQUIPMENT')} HM)
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
                                TOTAL: {totalEquipment} Eq ({hmForFront} HM)
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Box>
            );
          })}
          <ModalTransferList open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSaveItems} type={modalType} data={modalData} enableIncompleteFilter={modalType === 'produccion'} />

          <SubcontractPickerModal
            open={subcontractModal.open}
            onClose={() => setSubcontractModal({ ...subcontractModal, open: false })}
            onSave={handleAddSubcontract}
            resourceType={subcontractModal.resourceType}
            projectId={project || 0}
            section={activity.activity.section || ''}
            alreadyAddedIds={getSubcontractRows(workFronts[subcontractModal.frontIndex], subcontractModal.resourceType)
              .map(row => row.subcontractor.id)}
          />
          {(status !== 'view') && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', bgcolor: '#D9D9D9', p: 2 }}>
              <Button variant="contained" color="primary" onClick={handleAddWorkFront}>Agregar Frente</Button>
            </Box>
          )}
        </Collapse>

        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
            ¿Eliminar actividad completa?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Estás a punto de eliminar la actividad <strong>{activity.activityName}</strong>.
              Se perderá todo el avance ingresado, así como el personal y los equipos asignados a todos sus frentes de trabajo. ¿Deseas continuar?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmDeleteOpen(false)} color="primary" variant="outlined">
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setConfirmDeleteOpen(false);
                if (onRemove) onRemove();
              }}
              color="error"
              variant="contained"
              autoFocus
            >
              Sí, eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

export default ActivityDetails;